import { BigFilledButton, Div } from "components/Base";
import PasswordField from "components/Global/Inputs/Password";
import NotFound from "components/PortalV2/NotFound";
import React, { useCallback } from "react";
import CSSModules from "react-css-modules";
import PasswordChecklist from "react-password-checklist";
import { Style } from "react-style-tag";
import { noop } from "utils/General";
import PasswordChecklistConfig from "utils/password-checklist-config";
import css from "./styles.scss";

const View = CSSModules(
  ({
    email,
    error,
    errorMessages,
    eventDetails,
    firstName,
    handleSubmit,
    identityProviders,
    isFetching,
    isVirtualUser,
    isValid,
    lastName,
    location,
    onEmailChange,
    onFirstNameChange,
    onLastNameChange,
    onPasswordChange,
    onTryAgain,
    password,
    portal,
    processing,
    search,
    searchingUsers,
    userExists,
    shouldEnforceSso,
    customMessage,
    //
    logoImageUrl,
    backgroundImageUrl,
    logoImageAltText,
    backgroundImageAltText,
    buttonColor,
    buttonTextColor,
    backgroundColor,
    welcomeMessageFont,
    welcomeMessageSize,
    welcomeMessageColor,
    welcomeMessageText,
    cssCode
  }) => {
    if (errorMessages.length || (!isFetching && !portal.id)) {
      return <NotFound />;
    }
    const allowNewUsers = portal.event.enable_portal_signup;

    // @TODO: Remove the padding that is set on the logoContainer since
    // we are now using an image URL from the db
    const actionButton = (
      <button
        type="submit"
        styleName={!isValid ? "disabledButton" : "button"}
        disabled={!isValid || processing}
        style={{
          backgroundColor: buttonColor,
          color: buttonTextColor
        }}
      >
        {processing ? (
          <div styleName="buttonInner">
            <i className="fa fa-circle-o-notch" styleName="buttonLoadingIcon" />
            <span>
              {userExists === true && isVirtualUser === false
                ? "Logging in..."
                : "Processing..."}
            </span>
          </div>
        ) : (
          <div styleName="buttonInner">
            <span>Continue to portal</span>
          </div>
        )}
      </button>
    );

    const doSearch = useCallback(() => (searchingUsers ? noop() : search()), [
      search,
      searchingUsers,
      noop
    ]);

    return (
      <>
        {cssCode ? <Style>{cssCode}</Style> : null}
        <div
          styleName="wrapper"
          style={{
            backgroundColor: backgroundColor
          }}
        >
          <div
            styleName="background"
            aria-label={backgroundImageAltText}
            style={{
              backgroundImage: `url(${backgroundImageUrl})`
            }}
          />
          <div styleName="container">
            <div styleName="bodyTop">
              <div styleName="logoContainer">
                <img
                  styleName="logoImage"
                  src={logoImageUrl}
                  alt={logoImageAltText}
                />
              </div>
              <div styleName="bodyContent">
                <div styleName="bodyContentInner">
                  <div styleName="details">
                    <div styleName="superText">Welcome to</div>
                    <div>{portal.title}</div>
                    {welcomeMessageText && welcomeMessageText.length ? (
                      <div
                        className="lennd-wysiwyg-content portal-message-text"
                        style={{
                          fontWeight: "normal",
                          marginTop: "10px",
                          fontFamily: welcomeMessageFont,
                          fontSize: welcomeMessageSize,
                          color: welcomeMessageColor
                        }}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: welcomeMessageText
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div styleName="form">
                    {!eventDetails.enable_external_users &&
                      identityProviders.length === 0 && (
                        <div styleName="message">
                          Insufficient event authentication methods detected.
                          Please note that there are currently no active
                          authentication mechanisms in place for the event.
                          Don't hesitate to reach out your event team.
                        </div>
                      )}

                    {eventDetails.enable_external_users && shouldEnforceSso ? (
                      <form
                        styleName="formWrapper"
                        method="post"
                        onSubmit={handleSubmit}
                      >
                        <div styleName="fieldWrapper">
                          <div styleName="inputWrapper">
                            <div styleName="inputGroup">
                              <div styleName="inputLabel">Email</div>
                              <input
                                autoFocus
                                name="email"
                                onChange={onEmailChange}
                                placeholder="your_email_address@email.com"
                                required
                                styleName="input"
                                type="email"
                                value={email}
                                disabled={processing}
                              />
                            </div>
                          </div>

                          <Div
                            bg="warning2"
                            p={3}
                            bra={1}
                            mt={3}
                            mb={3}
                            tabIndex={0}
                          >
                            Your email domain requires you to login or sign up
                            using SSO.{" "}
                          </Div>

                          {error ? <div styleName="error">{error}</div> : ""}
                        </div>
                      </form>
                    ) : eventDetails.enable_external_users ? (
                      <form
                        styleName="formWrapper"
                        method="post"
                        onSubmit={handleSubmit}
                      >
                        <div styleName="fieldWrapper">
                          <div styleName="inputWrapper">
                            <div styleName="inputGroup">
                              <div styleName="inputLabel">Email</div>
                              <input
                                autoFocus
                                name="email"
                                onChange={onEmailChange}
                                placeholder="your_email_address@email.com"
                                required
                                styleName="input"
                                type="email"
                                value={email}
                                disabled={processing}
                              />
                            </div>
                            {userExists === true ||
                            (userExists === false && allowNewUsers) ? (
                              <div styleName="inputGroup">
                                <div styleName="inputLabel">Password</div>
                                <PasswordField
                                  className={css.input}
                                  disabled={processing}
                                  name="password"
                                  onChange={onPasswordChange}
                                  placeholder={
                                    userExists === false ||
                                    isVirtualUser === true
                                      ? "Create your password"
                                      : "Enter your password"
                                  }
                                  required
                                  value={password}
                                />

                                {userExists === false ||
                                isVirtualUser === true ? (
                                  <div
                                    style={{
                                      marginTop: "5px",
                                      marginBottom: "10px",
                                      fontSize: "14px",
                                      color: "#222"
                                    }}
                                  >
                                    <PasswordChecklist
                                      iconSize={12}
                                      rules={[
                                        "minLength",
                                        "specialChar",
                                        "number",
                                        "capital"
                                      ]}
                                      minLength={8}
                                      value={password}
                                      messages={PasswordChecklistConfig}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              ""
                            )}
                            {userExists === false ? (
                              allowNewUsers ? (
                                <div>
                                  <div styleName="inputGroup">
                                    <div styleName="inputLabel">First Name</div>
                                    <input
                                      name="firstName"
                                      onChange={onFirstNameChange}
                                      required
                                      styleName="input"
                                      type="text"
                                      value={firstName}
                                      disabled={processing}
                                    />
                                  </div>
                                  <div styleName="inputGroup">
                                    <div styleName="inputLabel">Last Name</div>
                                    <input
                                      name="lastName"
                                      onChange={onLastNameChange}
                                      required
                                      styleName="input"
                                      type="text"
                                      value={lastName}
                                      disabled={processing}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div styleName="message">
                                  {customMessage ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: customMessage
                                      }}
                                    />
                                  ) : (
                                    `Sorry, we could not find a record for this
                                  email. Contact your event administrator if you
                                  feel this is a mistake.`
                                  )}
                                  <br />
                                  <a
                                    styleName="actionText"
                                    onClick={onTryAgain}
                                    tabIndex="0"
                                  >
                                    Try Again
                                  </a>
                                </div>
                              )
                            ) : (
                              ""
                            )}
                          </div>
                          {userExists === null && (
                            <div
                              styleName="nextStepButton"
                              onClick={doSearch}
                              style={{
                                backgroundColor: buttonColor,
                                color: buttonTextColor
                              }}
                            >
                              {searchingUsers ? (
                                <i
                                  className="fa fa-circle-o-notch"
                                  styleName="processingIcon"
                                />
                              ) : (
                                "Next"
                              )}
                            </div>
                          )}
                          {userExists === true ||
                          (userExists === false && allowNewUsers) ? (
                            <div styleName="actions">
                              <div>{actionButton}</div>
                              {userExists === true ? (
                                <div>
                                  <a
                                    href={`/forgot?email=${email}`}
                                    styleName="actionForgotPassword"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                  >
                                    Forgot your password?
                                  </a>
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          ) : (
                            ""
                          )}

                          {shouldEnforceSso ? (
                            <Div
                              bg="warning2"
                              p={3}
                              bra={1}
                              mt={3}
                              mb={3}
                              tabIndex={0}
                            >
                              Your email domain requires you to login or sign up
                              using SSO.{" "}
                            </Div>
                          ) : null}
                          {error ? <div styleName="error">{error}</div> : ""}
                          {location.query.message ? (
                            <div styleName="message">
                              {location.query.message}
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      </form>
                    ) : null}
                    {identityProviders.length > 0 &&
                      eventDetails.enable_external_users && (
                        <div
                          style={{
                            textAlign: "center"
                          }}
                        >
                          - or -
                        </div>
                      )}
                    <div style={{ marginTop: "10px" }}>
                      <ul>
                        {identityProviders &&
                          identityProviders.map(identityProvider => {
                            return (
                              <li style={{ marginBottom: "10px" }}>
                                <BigFilledButton
                                  color="white"
                                  bg={buttonColor}
                                  style={{
                                    borderRadius: "20px",
                                    width: "100%"
                                  }}
                                  onClick={() => {
                                    localStorage.setItem(
                                      "redirect_url",
                                      window.location.href
                                    );
                                    window.location.replace(
                                      identityProvider.sso_url
                                    );
                                  }}
                                >
                                  Login with {identityProvider.vendor_app_name}
                                </BigFilledButton>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <a
            styleName="branding"
            href={`https://www.sessionboard.com/?source=portal_login&portal_id=${portal.id}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <img
              src="https://d2ams66j58uyd5.cloudfront.net/images/powered-by-sessionboard-gray.png"
              styleName="brandingImg"
              alt=""
            />
          </a>
        </div>
      </>
    );
  },
  css
);

export default View;
